import { Component, Inject } from "@angular/core";
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import { Observable } from "rxjs";
import { AdmConnectionService } from "../../api/services/adm-connection.service";
import { AdmConnectionInfo } from "../../api/models/adm-connection-info";
import { ConfirmDialogComponent } from "../confirm-dialog/confirm-dialog.component";
import { GlobalApp } from "../../tools/global";
import { DialogResult, DialogResultType } from "../DialogResult";

// Exactly one of the two ids is set: a single Agent Data Model, or every ADM a subscription owns.
export interface AdmConnectionDialogData {
  admLayoutId?: number;
  subscriptionId?: number;
}

@Component({
  selector: "adm-connection-dialog",
  templateUrl: "./adm-connection-dialog.component.html",
})
export class AdmConnectionDialogComponent {
  public info: AdmConnectionInfo | null = null;
  public loading = true;
  public errorMessage: string | null = null;
  public showSecret = false;
  public isAdmin = false;

  public Result: DialogResult = { ResultType: DialogResultType.Cancel, ResultData: null };

  constructor(
    public dialogRef: MatDialogRef<AdmConnectionDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: AdmConnectionDialogData,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private app: GlobalApp,
    private admConnectionService: AdmConnectionService
  ) {
    this.app.getAuthorizationStatus().subscribe((result) => (this.isAdmin = !!result.isAdmin));
    this.load();
  }

  get isSubscriptionLevel(): boolean {
    return !!this.data.subscriptionId;
  }

  private load() {
    this.loading = true;
    const request = this.isSubscriptionLevel
      ? this.admConnectionService.admConnectionGetSubscriptionConnectionInfo(this.data.subscriptionId)
      : this.admConnectionService.admConnectionGetLayoutConnectionInfo(this.data.admLayoutId);

    request.subscribe({
      next: (info) => {
        this.info = info;
        this.loading = false;
      },
      error: () => {
        this.errorMessage = "Connection details are not available yet. They appear once the Agent Data Model has been created.";
        this.loading = false;
      },
    });
  }

  get secretDisplay(): string {
    if (!this.info || !this.info.clientSecret) return "";
    return this.showSecret ? this.info.clientSecret : "•".repeat(16);
  }

  // Snippets that carry the secret (ADO.NET/ODBC/JDBC) are copied as-is; the display shows them masked unless the
  // user chose to reveal the secret.
  mask(snippet: string | undefined): string {
    if (!snippet) return "";
    if (this.showSecret || !this.info || !this.info.clientSecret) return snippet;
    return snippet.split(this.info.clientSecret).join("<secret hidden>");
  }

  copy(text: string | undefined) {
    if (!text) return;
    navigator.clipboard.writeText(text).then(
      () => this.snackBar.open("Copied", null, { duration: 2000 }),
      () => this.snackBar.open("Could not copy - select and copy manually", null, { duration: 4000 })
    );
  }

  rotateSecret() {
    ConfirmDialogComponent.showDialog(
      this.dialog,
      "Rotate Secret?",
      "A new secret is generated. Every connection using the current secret must be updated with the new one."
    ).subscribe((result) => {
      if (result.ResultType !== DialogResultType.Ok) return;

      const request = this.isSubscriptionLevel
        ? this.admConnectionService.admConnectionRotateSubscriptionSecret(this.data.subscriptionId)
        : this.admConnectionService.admConnectionRotateLayoutSecret(this.data.admLayoutId);

      request.subscribe({
        next: () => {
          this.snackBar.open("Secret rotated", null, { duration: 4000 });
          this.showSecret = true;
          this.load();
        },
        error: () => this.snackBar.open("Could not rotate the secret", null, { duration: 5000 }),
      });
    });
  }

  onClose(): void {
    this.dialogRef.close(this.Result);
  }

  static showDialog(dialog: MatDialog, title: string, data: AdmConnectionDialogData): Observable<DialogResult> {
    return dialog.open(AdmConnectionDialogComponent, { disableClose: false, width: "860px", data: { ...data, title } }).afterClosed();
  }
}
